import { createComponent, ElementWithProps } from '@carvana/merch-components';
import { SearchSource } from '@carvana/merch-contracts';
import MerchSearchBar from '@carvana/merch-search-bar';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useContext, useEffect, useState } from 'react';
import HideOnNative from '@/common/HideOnNative';
import StickyHeaderSentinel from '@/srp/MainLayout/StickyHeaderSentinel';
import TitleTag from '@/srp/SEOSection/TitleTag';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { useIsDesktop, useIsMobile } from '~/context/common/DeviceContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import { useSearchSource } from '~/context/srp/SearchEventsDataContext';
import { StickySearchContext } from '~/context/srp/StickySearchProvider';
import useSendToShiftDigital from '~/hooks/dealership/useSendToShiftDigital';
import BackgroundOverlay from './BackgroundOverlay';
import MobileFiltersCta from './MobileFiltersCta';
import useSearchBarProps from './useSearchBarProps';

export const SearchInputWrapper = createComponent('div', {
  className: 'relative z-10 flex w-full gap-md'
});

export const StickyWrapper = createComponent<
  ElementWithProps<{ isDealership: boolean; isSticky: boolean }>
>('div', ({ isDealership, isSticky }) => ({
  className: cx(
    'sticky top-0 z-1 flex flex-col justify-between px-2xl py-xl sm:top-xl sm:mx-3xl sm:rounded-md sm:px-xl md:mx-0 md:w-full md:p-2xl',
    'before:absolute before:-top-xl before:left-0 before:right-0 before:-z-10 before:h-full before:bg-gradient-to-b before:from-background-default before:from-5% before:transition-opacity before:duration-150',
    'after:absolute after:inset-0 after:z-1 after:transition-all after:duration-150 sm:after:rounded-md',
    isDealership && 'mb-xl md:mb-2xl',
    isSticky
      ? 'before:opacity-100 after:bg-background-medium'
      : 'before:opacity-0 after:bg-background-primary-subtle'
  )
}));

const SearchBarModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SearchBarModal" */ '@/srp/Modals/SearchBarModal'
    ),
  { ssr: false }
);

const shouldClearInput = (searchSource: SearchSource) => {
  switch (searchSource) {
    case SearchSource.DefaultSuggestion:
    case SearchSource.RecentSearch:
    case SearchSource.SavedSearch:
    case SearchSource.TextSearch:
      return false;
    default:
      return true;
  }
};

export default function SearchBar() {
  const [inputValue, setInputValue] = useState('');
  const searchBarProps = useSearchBarProps();
  const searchSource = useSearchSource();
  const { isStickySearchBar } = useContext(StickySearchContext);
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const isDealership = useIsDealershipPage();
  const sendToShiftDigital = useSendToShiftDigital();

  useEffect(() => {
    if (shouldClearInput(searchSource)) {
      setInputValue('');
    }
  }, [searchSource]);

  useEffect(() => {
    if (!inputValue) return;

    const t = setTimeout(() => {
      sendToShiftDigital(ShiftDigitalEventName.TypedSearch, {
        typedSearchContent: inputValue
      });
    }, 500);

    return () => {
      clearTimeout(t);
    };
  }, [inputValue, sendToShiftDigital]);

  return (
    <>
      <HideOnNative>
        <TitleTag className={isStickySearchBar ? 'hidden' : ''} />
      </HideOnNative>
      <StickyHeaderSentinel />
      <StickyWrapper
        isDealership={isDealership}
        isSticky={isStickySearchBar}
        data-testid="search-bar"
        id="search-wrapper"
      >
        <BackgroundOverlay />
        <SearchInputWrapper id="search-bar-wrapper">
          <div className="flex-1">
            <MerchSearchBar
              inputValue={inputValue}
              setInputValue={setInputValue}
              {...searchBarProps}
            />
          </div>
          {!isDesktop && <MobileFiltersCta />}
        </SearchInputWrapper>
        {isMobile && (
          <SearchBarModal
            inputValue={inputValue}
            setInputValue={setInputValue}
            {...searchBarProps}
          />
        )}
      </StickyWrapper>
    </>
  );
}
